import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableContainer,
  TableCell,
  Paper,
} from "@mui/material";
import { tableCellClasses } from "@mui/material/TableCell";
import DateConverter from "../../../utils/DateConverter";
import {
  PrimaryColor,
  TableBodyGrey,
  TableHeaderGrey,
  TableRowHover,
} from "../../../shared/styles/colors";
import DatabaseEntities from "../../../model/enum/DatabaseEntities";
import { Typography, Button } from "@material-ui/core";
import { TablePagination } from "@mui/material";
import { Spinner } from "../../../components";
import { ToastContainer } from "react-toastify";
import ApiService from "../../../service/ApiService";
import Bundle from "../../../model/bundle";
import CachedIcon from "@mui/icons-material/Cached";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import BundleDetailModal from "./BundleDetailModal";
import Pathnames from "../../../model/enum/Pathnames";
import PageResultResponse from "../../../model/pageResultResponse";
import useStyles from "../../styles/cardStyles";
import Misc from "../../../utils/Misc";

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: TableHeaderGrey,
    color: PrimaryColor,
    fontWeight: 700,
    whiteSpace: "nowrap",
    zIndex: 0,
    position: "static",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: TableBodyGrey,
    fontFamily: "monospace",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: TableRowHover,
  },
}));

const Bundles = () => {
  const classes = useStyles();
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("page", page.toString());
    url.searchParams.set("rowsPerPage", rowsPerPage.toString());
    window.history.pushState({}, Pathnames.PRODUCTS_SERVICES_BUNDLE_LIST, url);
  });

  // Get all the categories items from the database
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const queryParams = new URLSearchParams();
      queryParams.append("page", page.toString());
      queryParams.append("rowsPerPage", rowsPerPage.toString());

      await ApiService.get<PageResultResponse>(
        `${DatabaseEntities.BUNDLES}?${queryParams.toString()}`
      ).then((data) => {
        if (Misc.isArrayEmpty(data.items)) return;
        const { items, count } = data;
        setBundles(items);
        setTotalPages(count);
      });
      setLoading(false);
    };

    if (loading && Misc.isArrayEmpty(bundles)) fetchData();
  }, [bundles]);

  const resetList = () => {
    setPage(1);
    setRowsPerPage(10);
    setBundles([]);
    setLoading(true);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown> | null,
    pg: number
  ) => {
    setPage(pg + 1);
    scrollToTop();
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    let row = parseInt(event.target.value);
    setRowsPerPage(row);
    scrollToTop();
  };

  const scrollToTop = () => {
    setBundles([]);
    setLoading(true);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  const addIdToQueryPath = (id?: number) => {
    if (!id) return;

    const url = new URL(window.location.href);
    url.searchParams.set("bundleId", id.toString());
    window.history.pushState({}, Pathnames.PRODUCTS_SERVICES_BUNDLE_LIST, url);
  };

  const removeIdFromQueryPath = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("bundleId");
    window.history.replaceState({}, "", url.pathname + url.search);
  };

  return (
    <>
      <div className={classes.headerContainer}>
        <Typography className={classes.title}></Typography>
        <div className={classes.headerDiv}>
          <Button onClick={resetList}>
            <CachedIcon />
          </Button>
        </div>
      </div>
      <Paper sx={{ marginTop: "20px" }}>
        <TableContainer>
          <Table
            stickyHeader
            aria-label="customized table"
            padding="normal"
            size="medium"
          >
            <TableHead>
              <TableRow>
                <StyledTableCell align="right">Id</StyledTableCell>
                <StyledTableCell align="right">Name</StyledTableCell>
                <StyledTableCell align="right">Description</StyledTableCell>
                <StyledTableCell align="right">Is Ultra Bundle</StyledTableCell>
                <StyledTableCell align="right">Created At</StyledTableCell>
                <StyledTableCell align="right">
                  <MoreVertIcon />
                </StyledTableCell>
              </TableRow>
            </TableHead>
            {loading ? (
              <Spinner />
            ) : (
              <TableBody>
                {bundles.length > 0 ? (
                  bundles.map((row: Bundle) => (
                    <StyledTableRow key={row.id}>
                      <StyledTableCell align="right">{row.id}</StyledTableCell>
                      <StyledTableCell align="right">
                        {row.name}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {row.description}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {row.isUltra ? "Yes" : "No"}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {DateConverter.toFullDate(row.createdAt)}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        <BundleDetailModal
                          bundle={row}
                          onOpen={() => {
                            addIdToQueryPath(row?.id);
                          }}
                          onClose={() => {
                            removeIdFromQueryPath();
                          }}
                        />
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
                ) : (
                  <StyledTableCell align="right">No items</StyledTableCell>
                )}
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </Paper>
      <TablePagination
        component="div"
        count={totalPages}
        page={page - 1}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <ToastContainer />
    </>
  );
};

export default Bundles;
